import { circleRing } from '../shared/circle-geometry.js';
import type { SchemaInput } from '../shared/schema-input.js';
import { isFunction, isNumber, isSchemaRecord, isString } from '../shared/runtime-values.js';
import type { Point, Stroke } from '../types/app.js';
import type { PremiumSource } from '../shared/checkout-metadata.js';
import { getCircleRadiusKm } from './exact-radius-actions.js';
import {
  isServiceAreaFlowExperimentVariant,
  type ServiceAreaFlowExperimentVariant,
} from './service-area-flow-experiment.js';

export const SERVICE_AREA_HANDOFF_KEY = 'drawonamap:service-area-builder-handoff:v1';
export const SERVICE_AREA_ATTRIBUTION_KEY = 'drawonamap:service-area-builder-attribution:v1';
export const SERVICE_AREA_HANDOFF_FRAGMENT_KEY = 'service-area-handoff';

export type ServiceAreaEntrySource =
  | 'direct'
  | 'landing'
  | 'service_area_map'
  | 'embed_map'
  | 'radius_outcome'
  | 'circle_outcome';

export type ServiceAreaCircleOutcomeSource = Extract<
  ServiceAreaEntrySource,
  'radius_outcome' | 'circle_outcome'
>;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

interface StoredAttribution {
  source: ServiceAreaEntrySource;
  createdAt: number;
  journeyId?: string;
  variant?: ServiceAreaFlowExperimentVariant;
}

export interface ServiceAreaHandoff extends StoredAttribution {
  points: Point[];
}

interface CompactCircleHandoff extends StoredAttribution {
  center: Point;
  radiusKm: number;
}

export type ServiceAreaHandoffWriteStatus = 'written' | 'storage_error' | 'invalid_geometry';
export type ServiceAreaHandoffConsumeStatus =
  | 'consumed_storage'
  | 'consumed_fragment'
  | 'missing'
  | 'invalid'
  | 'expired'
  | 'storage_error';
export type ServiceAreaAttributionState = 'direct' | 'handoff' | 'url' | 'persisted';

export interface ServiceAreaHandoffWriteResult {
  status: ServiceAreaHandoffWriteStatus;
  handoff: ServiceAreaHandoff | null;
  fallbackHash: string | null;
}

export interface ServiceAreaHandoffConsumeResult {
  status: ServiceAreaHandoffConsumeStatus;
  handoff: ServiceAreaHandoff | null;
}

export interface ServiceAreaAttribution {
  source: ServiceAreaEntrySource;
  journeyId?: string;
  variant?: ServiceAreaFlowExperimentVariant;
  state: ServiceAreaAttributionState;
}

const ENTRY_SOURCE_VALUES = new Set<ServiceAreaEntrySource>([
  'direct',
  'landing',
  'service_area_map',
  'embed_map',
  'radius_outcome',
  'circle_outcome',
]);
const ATTRIBUTION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const EARTH_RADIUS_KM = 6371.0088;
const JOURNEY_ID_PATTERN = /^[a-f0-9-]{36}$/;

function isPoint(value: SchemaInput): value is Point {
  if (!isSchemaRecord(value)) return false;
  return isNumber(value.lat) && Number.isFinite(value.lat)
    && isNumber(value.lng) && Number.isFinite(value.lng)
    && Math.abs(value.lat) <= 90
    && Math.abs(value.lng) <= 180;
}

function normalizeJourneyId(value: SchemaInput): string | undefined {
  return isString(value) && JOURNEY_ID_PATTERN.test(value) ? value : undefined;
}

interface AttributionContextSource {
  journeyId?: SchemaInput;
  variant?: SchemaInput;
}

function storedContext(value: AttributionContextSource): Pick<StoredAttribution, 'journeyId' | 'variant'> {
  const journeyId = normalizeJourneyId(value.journeyId);
  const variant = isServiceAreaFlowExperimentVariant(value.variant) ? value.variant : undefined;
  const context: Pick<StoredAttribution, 'journeyId' | 'variant'> = {};
  if (journeyId) context.journeyId = journeyId;
  if (variant) context.variant = variant;
  return context;
}

function parseStoredAttribution(raw: string | null, now = Date.now()): StoredAttribution | null {
  if (!raw) return null;
  try {
    const parsed: SchemaInput = JSON.parse(raw);
    if (!isSchemaRecord(parsed)) return null;
    const value = parsed;
    const source = normalizeServiceAreaEntrySource(value.source);
    if (!isNumber(value.createdAt) || !Number.isFinite(value.createdAt)) return null;
    if (now - value.createdAt > ATTRIBUTION_MAX_AGE_MS) return null;
    return { source, createdAt: value.createdAt, ...storedContext(value) };
  } catch {
    return null;
  }
}

type ParsedHandoff =
  | { status: 'valid'; value: ServiceAreaHandoff }
  | { status: 'invalid' | 'expired'; value: null };

function parseStoredHandoff(raw: string, now: number): ParsedHandoff {
  try {
    const parsed: SchemaInput = JSON.parse(raw);
    if (!isSchemaRecord(parsed)) return { status: 'invalid', value: null };
    const value = parsed;
    const source = normalizeServiceAreaEntrySource(value.source);
    const points = Array.isArray(value.points) ? value.points.filter(isPoint) : [];
    if (
      (source !== 'radius_outcome' && source !== 'circle_outcome')
      || points.length < 3
      || !isNumber(value.createdAt)
      || !Number.isFinite(value.createdAt)
    ) return { status: 'invalid', value: null };
    if (now - value.createdAt > ATTRIBUTION_MAX_AGE_MS) {
      return { status: 'expired', value: null };
    }
    return {
      status: 'valid',
      value: {
        points,
        source,
        createdAt: value.createdAt,
        ...storedContext(value),
      },
    };
  } catch {
    return { status: 'invalid', value: null };
  }
}

function circleFromStroke(stroke: Stroke): { center: Point; radiusKm: number } | null {
  if (stroke.tool !== 'circle' || stroke.points.length < 2) return null;
  const first = stroke.points[0];
  const last = stroke.points[stroke.points.length - 1];
  if (!isPoint(first) || !isPoint(last)) return null;
  const radiusKm = getCircleRadiusKm(stroke);
  if (!Number.isFinite(radiusKm) || radiusKm <= 0) return null;
  return {
    center: {
      lat: (first.lat + last.lat) / 2,
      lng: (first.lng + last.lng) / 2,
    },
    radiusKm,
  };
}

export function circleToServiceAreaPoints(center: Point, radiusKm: number, segments = 48): Point[] {
  if (!isPoint(center) || !Number.isFinite(radiusKm) || radiusKm <= 0) return [];
  const count = Math.max(12, Math.min(96, Math.round(segments)));
  return circleRing(center, radiusKm, { earthRadiusKm: EARTH_RADIUS_KM, samples: count, closed: false, wrapLongitude: true });
}

function encodeCompactHandoff(value: CompactCircleHandoff): string {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

function decodeCompactHandoff(encoded: string, now: number): ParsedHandoff {
  try {
    const normalized = encoded.replaceAll('-', '+').replaceAll('_', '/');
    const padded = normalized + '='.repeat((4 - normalized.length % 4) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
    const parsed: SchemaInput = JSON.parse(new TextDecoder().decode(bytes));
    if (!isSchemaRecord(parsed)) return { status: 'invalid', value: null };
    const value = parsed;
    const source = normalizeServiceAreaEntrySource(value.source);
    if (
      (source !== 'radius_outcome' && source !== 'circle_outcome')
      || !isPoint(value.center)
      || !isNumber(value.radiusKm)
      || !Number.isFinite(value.radiusKm)
      || value.radiusKm <= 0
      || !isNumber(value.createdAt)
      || !Number.isFinite(value.createdAt)
    ) return { status: 'invalid', value: null };
    if (now - value.createdAt > ATTRIBUTION_MAX_AGE_MS) {
      return { status: 'expired', value: null };
    }
    const points = circleToServiceAreaPoints(value.center, value.radiusKm);
    if (points.length < 3) return { status: 'invalid', value: null };
    return {
      status: 'valid',
      value: {
        points,
        source,
        createdAt: value.createdAt,
        ...storedContext(value),
      },
    };
  } catch {
    return { status: 'invalid', value: null };
  }
}

function persistAttribution(
  attribution: StoredAttribution,
  sessionStorage: StorageLike,
  localStorage: StorageLike,
): void {
  const value = JSON.stringify(attribution);
  try { sessionStorage.setItem(SERVICE_AREA_ATTRIBUTION_KEY, value); } catch { /* optional */ }
  try { localStorage.setItem(SERVICE_AREA_ATTRIBUTION_KEY, value); } catch { /* optional */ }
}

export function createServiceAreaJourneyId(
  cryptoSource: Pick<Crypto, 'randomUUID'> | undefined = globalThis.crypto,
): string {
  if (cryptoSource && isFunction(cryptoSource.randomUUID)) return cryptoSource.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function normalizeServiceAreaEntrySource(value: SchemaInput): ServiceAreaEntrySource {
  // SAFETY: The adjacent allowlist, normalizer, or equality check limits this value to the stated union.
  return isString(value) && ENTRY_SOURCE_VALUES.has(value as ServiceAreaEntrySource)
    // SAFETY: The adjacent allowlist, normalizer, or equality check limits this value to the stated union.
    ? value as ServiceAreaEntrySource
    : 'direct';
}

export function serviceAreaCheckoutSource(source: ServiceAreaEntrySource): PremiumSource {
  if (source === 'radius_outcome') return 'service_area_radius';
  if (source === 'circle_outcome') return 'service_area_circle';
  if (source === 'landing') return 'service_area_landing';
  if (source === 'service_area_map') return 'service_area_map';
  if (source === 'embed_map') return 'service_area_embed';
  return 'service_area_builder';
}

export function writeServiceAreaHandoff(
  storage: StorageLike,
  stroke: Stroke,
  now = Date.now(),
  source: ServiceAreaCircleOutcomeSource = 'radius_outcome',
  journeyId = createServiceAreaJourneyId(),
  variant: ServiceAreaFlowExperimentVariant = 'control',
): ServiceAreaHandoffWriteResult {
  const circle = circleFromStroke(stroke);
  if (!circle) return { status: 'invalid_geometry', handoff: null, fallbackHash: null };
  const context = { journeyId, variant };
  const handoff: ServiceAreaHandoff = {
    points: circleToServiceAreaPoints(circle.center, circle.radiusKm),
    source,
    createdAt: now,
    ...context,
  };
  const compact: CompactCircleHandoff = {
    center: circle.center,
    radiusKm: circle.radiusKm,
    source,
    createdAt: now,
    ...context,
  };
  const fallbackHash = `#${SERVICE_AREA_HANDOFF_FRAGMENT_KEY}=${encodeCompactHandoff(compact)}`;
  const attribution: StoredAttribution = { source, createdAt: now, ...context };
  try {
    storage.setItem(SERVICE_AREA_HANDOFF_KEY, JSON.stringify(handoff));
    storage.setItem(SERVICE_AREA_ATTRIBUTION_KEY, JSON.stringify(attribution));
    return { status: 'written', handoff, fallbackHash };
  } catch {
    return { status: 'storage_error', handoff, fallbackHash };
  }
}

export function consumeServiceAreaHandoff({
  storage,
  hash = '',
  now = Date.now(),
}: {
  storage: StorageLike;
  hash?: string;
  now?: number;
}): ServiceAreaHandoffConsumeResult {
  let storageStatus: ServiceAreaHandoffConsumeStatus = 'missing';
  try {
    const raw = storage.getItem(SERVICE_AREA_HANDOFF_KEY);
    if (raw) {
      const parsed = parseStoredHandoff(raw, now);
      storageStatus = parsed.status === 'valid' ? 'consumed_storage' : parsed.status;
      try { storage.removeItem(SERVICE_AREA_HANDOFF_KEY); } catch { /* consumed in memory */ }
      if (parsed.status === 'valid') return { status: 'consumed_storage', handoff: parsed.value };
    }
  } catch {
    storageStatus = 'storage_error';
  }

  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const encoded = params.get(SERVICE_AREA_HANDOFF_FRAGMENT_KEY);
  if (encoded) {
    const parsed = decodeCompactHandoff(encoded, now);
    if (parsed.status === 'valid') return { status: 'consumed_fragment', handoff: parsed.value };
    return { status: parsed.status, handoff: null };
  }
  return { status: storageStatus, handoff: null };
}

export function resolveServiceAreaAttribution({
  search,
  sessionStorage,
  localStorage,
  handoff,
  now = Date.now(),
}: {
  search: string;
  sessionStorage: StorageLike;
  localStorage: StorageLike;
  handoff?: ServiceAreaHandoff | null;
  now?: number;
}): ServiceAreaAttribution {
  const requested = normalizeServiceAreaEntrySource(new URLSearchParams(search).get('source'));
  if (handoff) {
    const attribution: StoredAttribution = {
      source: handoff.source,
      createdAt: handoff.createdAt,
      ...storedContext(handoff),
    };
    persistAttribution(attribution, sessionStorage, localStorage);
    return { source: handoff.source, ...storedContext(handoff), state: 'handoff' };
  }
  const sessionValue = parseStoredAttribution((() => {
    try { return sessionStorage.getItem(SERVICE_AREA_ATTRIBUTION_KEY); } catch { return null; }
  })(), now);
  const localValue = parseStoredAttribution((() => {
    try { return localStorage.getItem(SERVICE_AREA_ATTRIBUTION_KEY); } catch { return null; }
  })(), now);
  if (requested !== 'direct') {
    const persisted = [sessionValue, localValue].find((value) => value?.source === requested);
    if (persisted) return { source: persisted.source, ...storedContext(persisted), state: 'persisted' };
    const attribution = { source: requested, createdAt: now, journeyId: createServiceAreaJourneyId() };
    persistAttribution(attribution, sessionStorage, localStorage);
    return { source: requested, journeyId: attribution.journeyId, state: 'url' };
  }
  if (sessionValue) return { source: sessionValue.source, ...storedContext(sessionValue), state: 'persisted' };
  if (localValue) return { source: localValue.source, ...storedContext(localValue), state: 'persisted' };
  const attribution = { source: 'direct' as const, createdAt: now, journeyId: createServiceAreaJourneyId() };
  persistAttribution(attribution, sessionStorage, localStorage);
  return { source: attribution.source, journeyId: attribution.journeyId, state: 'direct' };
}
